import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import { WorkflowBlockConditionsList } from "@/pages/workflows/edit/flow/WorkflowBlockConditionsList.tsx";
import { WorkflowBlockTreeRenderer } from "@/pages/workflows/edit/flow/WorkflowBlockTreeRenderer.tsx";
import { useWorkflowBlockConditions } from "@/pages/workflows/edit/flow/use-workflow-block-conditions.tsx";
import {
  useWorkflow,
  useWorkflowsSearch,
  useWorkflowUpdateBlockActionRunWorkflow,
  useWorkflowVersion,
} from "@/queries/workflows/workflow.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { WorkflowTreeBuilder } from "@/types/workflows/workflow-tree-builder.ts";
import { ApiWorkflowBlockActionRunWorkflow } from "@/types/workflows/workflow.action-notification.types.ts";
import { ApiWorkflowBlockActionTypes } from "@/types/workflows/workflow.base-enum.types.ts";
import { ApiWorkflowBlock, ApiWorkflowVersion } from "@/types/workflows/workflow.types.ts";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Button,
  Modal,
  ModalProps,
  Select,
  Stack,
  TabItem,
  Tabs,
  Title,
  useModals,
} from "@skailer-com/uikit";
import { useMemo, useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as yup from "yup";
import { WorkflowActionTimingEditor } from "./WorkflowActionTimingEditor.tsx";
import {
  getWorkflowActionTimingPayload,
  getWorkflowActionTimingValues,
  useWorkflows,
  WorkflowActionTimingFormFields,
} from "@/pages/workflows/workflow-hook.tsx";

export interface Props extends ModalProps {
  workflowVersion: ApiWorkflowVersion;
  block: ApiWorkflowBlock;
  actionRunWorkflow: ApiWorkflowBlockActionRunWorkflow;
}

interface FormValues extends WorkflowActionTimingFormFields {
  title: string | null;
  target_workflow_id: number | null;
}

export const WorkflowEditActionRunWorkflowModal = ({
  workflowVersion,
  block,
  actionRunWorkflow,
  ...props
}: Props) => {
  const { close } = useModals();
  const { id } = { ...props };
  const { t } = useTranslation();
  const { workflowActionTimingSchemaFields } = useWorkflows();
  const schemaObject = yup.object({
    title: yup.string().defined().nullable(),
    ...workflowActionTimingSchemaFields,
    target_workflow_id: yup
      .number()
      .nullable()
      .defined()
      .test("required", t("errors:field_required"), (value) => value != null),
  });

  const { data: workflows } = useWorkflowsSearch({ filter: { page_number: 1 } });

  const { conditions, handleAddCondition, handleEditCondition, handleDeleteCondition } =
    useWorkflowBlockConditions(workflowVersion, block);

  const {
    control,
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<FormValues>({
    mode: "onSubmit",
    resolver: yupResolver(schemaObject),
    values: {
      title: block.title ?? "",
      ...getWorkflowActionTimingValues(block),
      target_workflow_id: actionRunWorkflow.target_workflow?.id ?? null,
    },
  });

  const updateBlockActionRunWorkflow = useWorkflowUpdateBlockActionRunWorkflow(control);

  const targetWorkflowId = useWatch({ control, name: "target_workflow_id" });
  const { data: targetWorkflow } = useWorkflow(
    targetWorkflowId ?? undefined,
    targetWorkflowId != null
  );
  const { data: targetVersion } = useWorkflowVersion(
    targetWorkflow
      ? { workflowId: targetWorkflow.id, workflowVersionId: targetWorkflow.current_version_id }
      : null,
    targetWorkflow != null
  );
  const targetTree = useMemo(() => {
    if (!targetVersion) return null;
    return new WorkflowTreeBuilder(targetVersion.blocks, targetVersion.edges).buildTree();
  }, [targetVersion]);

  const handleSave = handleSubmit(async (data) => {
    try {
      await updateBlockActionRunWorkflow.mutateAsync({
        workflowId: workflowVersion.workflow_id,
        workflowVersionId: workflowVersion.id,
        workflowBlockId: block.id,
        payload: {
          title: data.title,
          ...getWorkflowActionTimingPayload(data),
          block_data: { target_workflow_id: data.target_workflow_id },
          conditions: conditions.map((x) => ({ ...x })),
        },
      });

      analyticsService.trackEvent(analyticEvents.workflows.blockEdited, {
        [analyticProperties.id]: workflowVersion.workflow_id,
        [analyticProperties.actionType]: ApiWorkflowBlockActionTypes.run_workflow,
      });

      close(id);
    } catch {
      // handle error
    }
  });

  const [activeTab, setActiveTab] = useState(0);

  return (
    <Modal
      {...props}
      variant={"side"}
      title={t("workflow:action_type_run_workflow")}
      layout={"stretch"}
      withCloser
      closeByEscEnabled
      size={"lg"}
      actions={
        <Stack gap={"sm"}>
          <ServerErrorField errors={errors} />
          <Button
            size={"lg"}
            className={"w-full"}
            type={"submit"}
            onClick={handleSave}
            isLoading={updateBlockActionRunWorkflow.isPending}
          >
            {t("common:save")}
          </Button>
        </Stack>
      }
    >
      <form onSubmit={handleSave}>
        <Tabs onActiveTabChange={setActiveTab} activeTab={activeTab}>
          <TabItem title={t("workflow:action_type_run_workflow")}>
            <Stack gap={"lg"} className={"py-6"}>
              <Controller
                render={({ field: { value, onChange } }) => (
                  <Select
                    label={t("workflow:action_type_run_workflow_target")}
                    emptyTitle={t("common:select")}
                    value={value}
                    required={true}
                    searchable={true}
                    options={(workflows?.items ?? []).map((workflow) => ({
                      id: workflow.id,
                      title: workflow.title,
                    }))}
                    onChange={onChange}
                    error={errors.target_workflow_id?.message}
                  />
                )}
                control={control}
                name={"target_workflow_id"}
              />
              {targetTree && targetVersion && (
                <Stack gap={"sm"}>
                  <Title header={t("workflow:action_type_run_workflow_steps")} size={"xs"} />
                  <div className={"pointer-events-none opacity-60"}>
                    <WorkflowBlockTreeRenderer
                      node={targetTree}
                      workflowVersion={targetVersion}
                      readOnly
                    />
                  </div>
                </Stack>
              )}
            </Stack>
          </TabItem>
          <TabItem title={t("workflow:conditions")}>
            <Stack gap={"lg"} className={"py-6"}>
              <WorkflowActionTimingEditor
                control={control}
                register={register}
                errors={errors}
                triggerDateAvailable={workflowVersion.trigger_schedule_rule !== null}
              />

              <WorkflowBlockConditionsList
                conditions={conditions}
                onAdd={handleAddCondition}
                onEdit={handleEditCondition}
                onDelete={handleDeleteCondition}
              />
            </Stack>
          </TabItem>
        </Tabs>
      </form>
    </Modal>
  );
};
